/// regex :
export const hasIdentificationNumberFormat = (identificationNumber: string) => {
  const regex = /^[0-9]{9}$/;
  return regex.test(identificationNumber);
};

export const hasPhoneFormatWithoutAreaCode = (phoneNumber: string) => {
  const regex = /^[0-9]{9}$/;
  return regex.test(phoneNumber);
};

export const hasPhoneFormatWithAreaCode = (phoneNumber: string) => {
  const regex = /^\+\d{2}\d{9}$/;
  return regex.test(phoneNumber);
};

export const hasMobilePhoneFormatWithoutAreaCode = (phoneNumber: string) => {
  const phone = /^[6-7-9]\d{8}$/;
  return phone.test(phoneNumber);
};

export const hasMobilePhoneFormatWithIndex = (phoneNumber: string) => {
  const phone = /^0[6-7-9]\d{8}$/;
  return phone.test(phoneNumber);
};

export const hasMobilePhoneFormatWithAreaCode = (phoneNumber: string) => {
  const regex = /^\+\d{2}[6-7-9]\d{8}$/;
  return regex.test(phoneNumber);
};

export const hasMobilePhoneFormatWithSpaces = (phoneNumber: string) => {
  const regex = /^0[6-7-9](?:\s*\d){8}$/;
  return regex.test(phoneNumber);
};

export const hasMobilePhoneFormat = (phone: string) => {
  return (
    hasMobilePhoneFormatWithoutAreaCode(phone) ||
    hasMobilePhoneFormatWithAreaCode(phone) ||
    hasMobilePhoneFormatWithIndex(phone)
  );
};

export const hasPasswordFormat = (password: string) => {
  const regex = /^(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/;
  return regex.test(password);
};

export const has8Char = (password: string) => {
  return password.length >= 8;
};

export const has1SpecialCharacter = (password: string) => {
  const regex = /[#?!@$%^&*-]/;
  return regex.test(password);
};

export const has1Number = (password: string) => {
  const regex = /[0-9]/;
  return regex.test(password);
};

export const isPin4Digits = (value: string) => /^[0-9]{4}$/.test(value.trim());

export const normalizeExcelHeader = (value: string): string =>
  value
    // Split accented characters into base letters and diacritics.
    .normalize("NFD")
    // Remove the diacritic marks produced by the normalization.
    .replace(/[\u0300-\u036f]/g, "")
    // Ignore spaces, punctuation, and symbols in column names.
    .replace(/[^a-zA-Z0-9]/g, "")
    // Compare headers without case sensitivity.
    .toLowerCase();

export const hasPlanningHourFormat = (planningHour: string): boolean => {
  const regexWithoutSeparator = /^(?:[01][0-9]|2[0-3])[0-5][0-9]$/;
  const regexWithSeparator = /^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/;
  return (
    regexWithoutSeparator.test(planningHour) ||
    regexWithSeparator.test(planningHour)
  );
};

export const formatPlanningHourFormat = (
  planningHour: string,
): string | null => {
  if (!hasPlanningHourFormat(planningHour)) return null;
  return planningHour.replace(":", "");
};

export const hasPlanningStartDateFormat = (
  planningStartDate: string,
): boolean => {
  const dateParts = planningStartDate.split(/[/-]/);
  if (dateParts.length !== 3) return false;

  const [firstDatePart, secondDatePart, yearPart] = dateParts;
  if (!/^[0-9]{1,2}$/.test(firstDatePart)) return false;
  if (!/^[0-9]{1,2}$/.test(secondDatePart)) return false;
  if (!/^[0-9]{2}$|^[0-9]{4}$/.test(yearPart)) return false;

  const day = yearPart.length === 2 ? secondDatePart : firstDatePart;
  const month = yearPart.length === 2 ? firstDatePart : secondDatePart;

  return (
    Number(day) >= 1 &&
    Number(day) <= 31 &&
    Number(month) >= 1 &&
    Number(month) <= 12
  );
};

export const formatPlanningStartDateFormat = (
  planningStartDate: string,
): [string, string, string] | null => {
  if (!hasPlanningStartDateFormat(planningStartDate)) return null;

  const [firstDatePart, secondDatePart, yearPart] =
    planningStartDate.split(/[/-]/);
  const day = yearPart.length === 2 ? secondDatePart : firstDatePart;
  const month = yearPart.length === 2 ? firstDatePart : secondDatePart;
  const year = yearPart.length === 2 ? `20${yearPart}` : yearPart;

  return [day.padStart(2, "0"), month.padStart(2, "0"), year];
};

export const hasPlanningMileageFormat = (planningMileage: string): boolean => {
  const regex = /^[0-9]+([,.][0-9]+)?$/;
  return regex.test(planningMileage);
};

export const formatPlanningMileageFormat = (
  planningMileage: string,
): string | null => {
  if (!hasPlanningMileageFormat(planningMileage)) return null;
  return planningMileage.replace(",", ".");
};

export const hasEmailFormat = (email: string) => {
  const regex = /^[\w.+-]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)*\.[a-zA-Z]{2,}$/;
  return regex.test(email);
};

export const hasBirthDateFormat = (birthDate: string) => {
  const regexBirthDate =
    /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/;
  return regexBirthDate.test(birthDate);
};

export const FORMAT_TEMPORARY_ACCEPTED = "_";

const hasRegistrationNumberFormatCore = (registrationNumber: string) => {
  const regexWithoutDashesSIV = /^[a-zA-Z]{2}[0-9]{3}[a-zA-Z]{2}$/;
  const regexWithDashesSIV = /^[a-zA-Z]{2}[-][0-9]{3}[-][a-zA-Z]{2}$/;

  const regexWithoutDashesFIN = /^[0-9]{2,4}[a-zA-Z]{2,3}9?[0-9]{2}$/;
  const regexWithDashesFIN = /^[0-9]{2,4}[-][a-zA-Z]{2,3}[-]9?[0-9]{2}$/;

  const regexMoroccanPlate = /^[0-9]{1,5}[a-zA-Z]{1}[0-9]{1,2}$/;
  const regexOldMoroccanPlate = /^[0-9]{4}[-][0-9]{2,3}$/;

  const regexGermanPlate = /^[a-zA-Z]{1,3}[-][a-zA-Z]{1,2}[-][0-9]{1,4}$/;

  // reject if more than 1 x next to each other
  /*  if (registrationNumber && registrationNumber.toLowerCase().includes("xx"))
   return false;*/

  return (
    regexWithoutDashesSIV.test(registrationNumber) ||
    regexWithDashesSIV.test(registrationNumber) ||
    regexWithoutDashesFIN.test(registrationNumber) ||
    regexWithDashesFIN.test(registrationNumber) ||
    regexMoroccanPlate.test(registrationNumber) ||
    regexOldMoroccanPlate.test(registrationNumber) ||
    regexGermanPlate.test(registrationNumber)
  );
};

export const hasRegistrationNumberFormat = (registrationNumber: string) => {
  if (!registrationNumber) return false;
  if (hasRegistrationNumberFormatCore(registrationNumber)) return true;

  if (!registrationNumber.endsWith(FORMAT_TEMPORARY_ACCEPTED)) return false;

  return hasRegistrationNumberFormatCore(
    registrationNumber.slice(
      0,
      registrationNumber.lastIndexOf(FORMAT_TEMPORARY_ACCEPTED),
    ),
  );
};

export const hasIdentifierFormat = (identifier: string) => {
  const regexIdentifierWithDash = /^QIV_[a-zA-Z0-9]{6}$/;
  const regexIdentifierWithoutDash = /^QIV[a-zA-Z0-9]{6}$/;
  const regexIdentifierWithSpace = /^QIV [a-zA-Z0-9]{6}$/;
  return (
    regexIdentifierWithDash.test(identifier) ||
    regexIdentifierWithoutDash.test(identifier) ||
    regexIdentifierWithSpace.test(identifier)
  );
};

export const identifierFormatter = (identifier: string) => {
  const regexIdentifierWithoutDash = /^QIV[a-zA-Z0-9]{6}$/;
  const regexIdentifierWithSpace = /^QIV [a-zA-Z0-9]{6}$/;

  if (identifier.match(regexIdentifierWithoutDash)) {
    identifier = identifier.slice(0, 3) + "_" + identifier.slice(3, 9);
  } else if (identifier.match(regexIdentifierWithSpace)) {
    identifier = identifier.replace(" ", "_");
  }

  return identifier;
};

export const hasIbanFormat = (iban: string): boolean => {
  return iban.length >= 14 && iban.length <= 34;
};

/// formatter:

export const addPhoneAreaCode = (phone: string) => {
  const phoneFormatted = "+33" + phone;
  return phoneFormatted;
};

export const phoneNumberFormatter = (phone: string) => {
  if (hasMobilePhoneFormatWithAreaCode(phone)) {
    phone = phone.substring(3);
  } else if (hasMobilePhoneFormatWithIndex(phone)) {
    phone = phone.substring(1);
  }
  return phone;
};

export const formatMobilePhoneWithoutSpaces = (phoneNumber: string) => {
  return addPhoneAreaCode(phoneNumber.replaceAll(" ", "").substring(1));
};

export const registrationNumberFormatter = (registrationNumber: string) => {
  const regexSIV = /^[a-zA-Z]{2}[0-9]{3}[a-zA-Z]{2}$/;

  const regexFIN2digits3letters2digits = /^[0-9]{2}[a-zA-Z]{3}[0-9]{2}$/;
  const regexFIN3digits3letters2digits = /^[0-9]{3}[a-zA-Z]{3}[0-9]{2}$/;
  const regexFIN4digits3letters2digits = /^[0-9]{4}[a-zA-Z]{3}[0-9]{2}$/;
  const regexFIN2digits3letters3digits = /^[0-9]{2}[a-zA-Z]{3}[0-9]{3}$/;
  const regexFIN3digits3letters3digits = /^[0-9]{3}[a-zA-Z]{3}[0-9]{3}$/;
  const regexFIN4digits3letters3digits = /^[0-9]{4}[a-zA-Z]{3}[0-9]{3}$/;

  const regexFIN2digits2letters2digits = /^[0-9]{2}[a-zA-Z]{2}[0-9]{2}$/;
  const regexFIN3digits2letters2digits = /^[0-9]{3}[a-zA-Z]{2}[0-9]{2}$/;
  const regexFIN4digits2letters2digits = /^[0-9]{4}[a-zA-Z]{2}[0-9]{2}$/;
  const regexFIN2digits2letters3digits = /^[0-9]{2}[a-zA-Z]{2}[0-9]{3}$/;
  const regexFIN3digits2letters3digits = /^[0-9]{3}[a-zA-Z]{2}[0-9]{3}$/;
  const regexFIN4digits2letters3digits = /^[0-9]{4}[a-zA-Z]{2}[0-9]{3}$/;

  const formatRegistrationNumber = (
    part1Length: number,
    part2Length: number,
    part3Length: number,
  ) => {
    return (
      registrationNumber.slice(0, part1Length) +
      "-" +
      registrationNumber.slice(part1Length, part1Length + part2Length) +
      "-" +
      registrationNumber.slice(
        part1Length + part2Length,
        part1Length + part2Length + part3Length,
      )
    );
  };

  if (registrationNumber.match(regexSIV)) {
    registrationNumber = formatRegistrationNumber(2, 3, 2);
  } else if (registrationNumber.match(regexFIN2digits3letters2digits)) {
    registrationNumber = formatRegistrationNumber(2, 3, 2);
  } else if (registrationNumber.match(regexFIN3digits3letters2digits)) {
    registrationNumber = formatRegistrationNumber(3, 3, 2);
  } else if (registrationNumber.match(regexFIN4digits3letters2digits)) {
    registrationNumber = formatRegistrationNumber(4, 3, 2);
  } else if (registrationNumber.match(regexFIN2digits3letters3digits)) {
    registrationNumber = formatRegistrationNumber(2, 3, 3);
  } else if (registrationNumber.match(regexFIN3digits3letters3digits)) {
    registrationNumber = formatRegistrationNumber(3, 3, 3);
  } else if (registrationNumber.match(regexFIN4digits3letters3digits)) {
    registrationNumber = formatRegistrationNumber(4, 3, 3);
  } else if (registrationNumber.match(regexFIN2digits2letters2digits)) {
    registrationNumber = formatRegistrationNumber(2, 2, 2);
  } else if (registrationNumber.match(regexFIN3digits2letters2digits)) {
    registrationNumber = formatRegistrationNumber(3, 2, 2);
  } else if (registrationNumber.match(regexFIN4digits2letters2digits)) {
    registrationNumber = formatRegistrationNumber(4, 2, 2);
  } else if (registrationNumber.match(regexFIN2digits2letters3digits)) {
    registrationNumber = formatRegistrationNumber(2, 2, 3);
  } else if (registrationNumber.match(regexFIN3digits2letters3digits)) {
    registrationNumber = formatRegistrationNumber(3, 2, 3);
  } else if (registrationNumber.match(regexFIN4digits2letters3digits)) {
    registrationNumber = formatRegistrationNumber(4, 2, 3);
  }

  return registrationNumber.toUpperCase();
};

export const transformRegistrationNumber = (input: string) => {
  const matchSIV = RegExp(/^([A-Z]{2})-?(\d{3})-?([A-Z]{2})$/i).exec(input);
  const matchFIN = RegExp(/^([0-9]{2,4})-?([A-Z]{2,3})-?([0-9]{2,3})$/i).exec(
    input,
  );
  if (matchSIV) {
    return `${matchSIV[1]}-${matchSIV[2]}-${matchSIV[3]}`;
  } else if (matchFIN) {
    return `${matchFIN[1]}-${matchFIN[2]}-${matchFIN[3]}`;
  }
  return input;
};

export const dateFormatterDay = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    day: "2-digit",
  });
  return formatter.format(date);
};

export const dateFormatterLongMonthYear = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    month: "long",
    year: "numeric",
  });
  const formatted = formatter.format(date);
  if (!formatted) {
    return formatted;
  }
  return formatted.charAt(0).toLocaleUpperCase("fr-FR") + formatted.slice(1);
};

export const dateFormatterMonthYear = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    month: "short",
    year: "numeric",
  });
  return formatter.format(date);
};

export const dateFormatterDayMonth = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    month: "short",
    day: "2-digit",
  });
  return formatter.format(date);
};

export const dateFormatterDayMonthLong = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    month: "long",
    day: "2-digit",
  });
  return formatter.format(date);
};

export const dateFormatterDayShortMonthYear = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return formatter.format(date);
};

export const dateFormatterDayMonthYear = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  return formatter.format(date);
};

export const dateFormatterDayMonthYear2Digits = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
  return formatter.format(date);
};

export const dateFormatterDayMonthYearLong2Digits = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return formatter.format(date);
};

export const dateFormatterSimpleDate = (date: Date) =>
  formatDate(date, {
    year: "numeric",
    day: "2-digit",
    month: "2-digit",
  });

const formatDate = (
  date: Date,
  options: {
    year?: "numeric" | "2-digit";
    month?: "numeric" | "2-digit" | "long" | "short";
    day?: "numeric" | "2-digit";
  },
) => new Intl.DateTimeFormat("fr", options).format(date);

export const dateFormatterHourMinute = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return formatter.format(date);
};

export const dateFormatterDayMonthYearHourMinute = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return formatter.format(date);
};

export const dateFormatterDayLongMonthYearHourMinuteSecond = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  return formatter.format(date);
};

export const dateFormatterDayMonthYearHourMinute2Digits = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  return formatter.format(date);
};

export const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const uppercaseFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const dateFormatterDayMonthYearAndHourMinute2Digits = (date: Date) => {
  const formatter =
    dateFormatterDayMonthYear2Digits(date) +
    " à " +
    dateFormatterHourMinute(date).replaceAll(":", "h");
  return formatter;
};

export const dateFormatterDayMonthLongYearAndHourMinute2Digits = (
  date: Date,
) => {
  const formatter =
    dateFormatterDayMonthYearLong2Digits(date) +
    " à " +
    dateFormatterHourMinute(date).replaceAll(":", "h");
  return formatter;
};

export const formattedZipFolderFileName = (prefix: string, date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const formattedDateTemp = formatter.format(date);
  const formattedDate = formattedDateTemp.split("/").join("_");
  return `${prefix}_${formattedDate}.zip`;
};

export const dateFormatterWeekDayDayMonthYear = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  return formatter.format(date);
};

export const dateFormatterWeekDayDayMonth = (date: Date) => {
  const formatter = new Intl.DateTimeFormat("fr", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });
  return formatter.format(date);
};

export const fromToDateFormatterDayMonthLongYear = (from: Date, to: Date) => {
  return `du ${dateFormatterDayMonth(from)} au ${dateFormatterDayMonthYear(
    to,
  )}`;
};

export const regexUUID =
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

export const validateUUID = (candidate: string) => regexUUID.test(candidate);

export const transformStrDateToMMYY = (date: string) => {
  // Input: "décembre 2024" -> Output: "12/01/2024"
  // Input: "du 10 oct. au 15 octobre 2025" -> Output: "15/10/2025"
  const monthsMap: { [key: string]: string } = {
    janvier: "01",
    janv: "01",
    février: "02",
    févr: "02",
    mars: "03",
    avril: "04",
    avr: "04",
    mai: "05",
    juin: "06",
    juillet: "07",
    juil: "07",
    août: "08",
    septembre: "09",
    sept: "09",
    octobre: "10",
    oct: "10",
    nov: "11",
    novembre: "11",
    décembre: "12",
    déc: "12",
  };

  if (date.includes("du") && date.includes("au")) {
    const parts = date.trim().split(" ");
    // Find the index of "au" to extract the second date
    const auIndex = 3;

    const dayStr = parts[auIndex + 1];
    const monthName = parts[auIndex + 2]?.replace(".", "");
    const yearStr = parts[auIndex + 3];

    const month = monthsMap[monthName?.toLowerCase() || ""];

    if (!month || !dayStr || !yearStr) return date; // Return original value if parsing fails

    return `${month}/${dayStr}/${yearStr}`;
  }

  // Original format: "décembre 2024"
  const parts = date.trim().split(" ");
  if (parts.length !== 2) return date; // Return original value if format is incorrect

  const [monthName, yearStr] = parts;
  const month = monthsMap[monthName.toLowerCase()];

  if (!month || !yearStr) return date; // Return original value if parsing fails

  return `${month}/01/${yearStr}`;
};
